import heic2any from 'heic2any';

function isHeicFile(file) {
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
}

function readAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read image file'));
    reader.readAsDataURL(blob);
  });
}

async function convertViaCanvas(file) {
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.85);
  } finally {
    bitmap.close?.();
  }
}

async function convertViaHeic2Any(file) {
  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.85,
  });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  return readAsDataUrl(blob);
}

export function isPdfFile(file) {
  return (file.type || '').toLowerCase() === 'application/pdf' || (file.name || '').toLowerCase().endsWith('.pdf');
}

const PDF_DATA_PREFIX = 'data:application/pdf';

// True for a report note whose attachment is a PDF: either saved (the API
// sends image_type) or still local (image is the file's data URL).
export function isPdfAttachment(node) {
  return node?.image_type === 'application/pdf' || `${node?.image || ''}`.startsWith(PDF_DATA_PREFIX);
}

// A temporary blob URL for a data URL. The caller revokes it when done.
export function dataUrlToBlobUrl(url) {
  const [header, data] = url.split(',');
  const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: header.slice(5).split(';')[0] }));
}

// Opens an attachment in a new tab. Browsers refuse to navigate to data URLs,
// so a local (unsaved) file is opened through a temporary blob URL.
export function openAttachment(url) {
  if (!`${url}`.startsWith('data:')) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  const blobUrl = dataUrlToBlobUrl(url);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

/**
 * Encode an attachment File as a data URL. HEIC/HEIF are converted to JPEG
 * first so previews and stored report notes work in all browsers. PDFs keep
 * their format and are stored as PDFs.
 */
export async function encodeImageFile(file) {
  if (isPdfFile(file)) {
    // Some systems report no type for a .pdf; the API reads it from the data URL.
    return readAsDataUrl(new Blob([file], { type: 'application/pdf' }));
  }

  if (!isHeicFile(file)) {
    return readAsDataUrl(file);
  }

  // Safari can decode HEIC natively; canvas re-encodes as JPEG.
  // Chrome/Firefox need heic2any (wasm).
  try {
    return await convertViaCanvas(file);
  } catch (canvasError) {
    try {
      return await convertViaHeic2Any(file);
    } catch (heicError) {
      console.error('HEIC conversion failed', { canvasError, heicError });
      throw heicError;
    }
  }
}
